import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ActionButtonsGroup from '../containers/ActionButtonsGroup'
import util from '../../helpers/util.helper'
import hljs from 'highlight.js/lib/core';
import java from 'highlight.js/lib/languages/java';
import xml from 'highlight.js/lib/languages/xml';
import sql from 'highlight.js/lib/languages/sql';
import json from 'highlight.js/lib/languages/json';
import { AppContext } from '../../context'
import { Typography } from '@mui/material'
import { ActionButtonItemProps, TextContainerProps } from '../../types'
import { FILE_TEXT_IFRAME } from '../../config'

hljs.registerLanguage('java', java);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('xsd', xml);
hljs.registerLanguage('wsdl', xml);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('json', json);

const currentLanguages = ['java','xml', 'xsd', 'wsdl', 'sql', 'json']

export default function TextContainer({ file, filename, fileExtension }: Readonly<TextContainerProps>) {
  const appContext = useContext(AppContext)
  const [text, setText] = useState('')
  const numberLines = useMemo<number>(() => text.split('\n').length, [text])
  const showByIframe = FILE_TEXT_IFRAME.includes(fileExtension || '')
  const textDivRef = useRef<HTMLDivElement>(null)

  const actions = useMemo<ActionButtonItemProps[]>(() => [
    {
      title: 'Copiar', action: () => {
        navigator.clipboard.writeText(text)
          .then(() => {
            appContext?.openSnackBar(`${filename} copiado a papelera`)
          })
          .catch(() => {
            appContext?.openSnackBar(`Se presento un problema al copiar a papelera`)
          })
      }, icon: 'copy'
    },
    {
      title: 'Descargar', action: () => util.downloadFile(filename, file), icon: 'download'
    }
  ], [text, filename, file])

  useEffect(() => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      let result = reader.result
      if (typeof result === 'string')
        setText(result)
    }, false);

    if (file) {
      reader.readAsText(file);
    }
    //file?.text().then(setText)
  }, [file])

  useEffect(() => {
    if (textDivRef.current && text && fileExtension) {
      textDivRef.current.innerHTML = getCodeHtml(text, fileExtension)
      textDivRef.current.role = 'code'
    }
  }, [text, fileExtension])

  return (
    <div className="txt-edit">
      <div className='d-flex-center' style={{ padding: '0.5rem 0' }}>
        <ActionButtonsGroup className='m-r-auto' items={actions} />
        <Typography variant='caption' className='m-l-auto'>
          {text.length} Caracteres {!showByIframe && `| ${numberLines} Líneas`}
        </Typography>
      </div>
      <div className="txt-edit-container scroll code-hl ">
        {showByIframe ? <iframe
          className='mobile-doc-file'
          title={filename}
          src={`data:text/${fileExtension},${text}`}
        /> : <>
          <div className="monospace txt-edit-linenumber gray">
            {Array(numberLines + 2).fill(1).map((_, idx) => <Fragment key={idx}>{idx > 0 ? idx : ''} <br /></Fragment>)}
          </div>
          <pre className="txt-edit-file monospace scroll" style={{ height: `calc(${numberLines * 14}px + 1rem)`, resize: 'none' }}>
            <code id='prefile' className={`hljs`} ref={textDivRef}></code>
          </pre>
        </>}
      </div>
    </div>
  )
}

function getCodeHtml(text: string, extension: string) {
  try {
    return hljs.highlight(text, { language: currentLanguages.includes(extension) ? extension : 'auto' }).value
  } catch (e) {
    return text
  }
}